import { TimeSlot } from "@src/appV2/redesign/Shift/Open/types";
import { format, parseISO } from "date-fns";

import { DEFAULT_DISTANCE_IN_MILES_FILTER } from "../constants";

/**
 * Formats license filter summary text
 * Returns "All" for empty array, single license name, or first license + count for multiple
 */
export function getDetailsFilterSummary(licenses: string[]) {
  switch (licenses.length) {
    case 0: {
      return "All";
    }

    case 1: {
      return `${licenses[0]} roles`;
    }

    default: {
      return `${licenses[0]} + ${licenses.length - 1} roles`;
    }
  }
}

/**
 * Formats distance filter summary with "mi" suffix
 */
export function getDistanceFilterSummary(distance: number) {
  return `${distance}mi`;
}

/**
 * Formats date selection summary
 * Returns empty string for no dates, formatted dates for same month, or count for multiple months
 */
function getDatesSummary(dates: string[]) {
  if (dates.length > 3) {
    return `${dates.length} dates`;
  }

  if (dates.length === 0) {
    return "";
  }

  // Check if all dates are in the same month
  const dateObjects = dates.map((dateString) => parseISO(dateString));
  const firstDate = dateObjects[0];
  const firstMonth = firstDate.getMonth();
  const firstYear = firstDate.getFullYear();

  const allSameMonth = dateObjects.every(
    (date) => date.getMonth() === firstMonth && date.getFullYear() === firstYear
  );

  if (allSameMonth) {
    const monthName = format(firstDate, "MMM");
    const dayNumbers = dateObjects.map((date) => date.getDate()).sort((a, b) => a - b);
    const dayString = dayNumbers.join(", ");

    return `${monthName} ${dayString}`;
  }

  // Different months, return count
  return `${dates.length} dates`;
}

const SLOT_TO_TEXT = {
  [TimeSlot.AM]: "Day",
  [TimeSlot.PM]: "Eve",
  [TimeSlot.NOC]: "NOC",
};

/**
 * Formats time slots into display text joined with "+"
 */
function getTimeSlotsSummary(shiftTimeSlots: TimeSlot[]) {
  if (shiftTimeSlots.length === 0) {
    return "";
  }

  return shiftTimeSlots.map((slot) => SLOT_TO_TEXT[slot]).join("+");
}

/**
 * Formats when filter summary combining dates and time slots
 * Returns string for no time slots, or object with primary/secondary for time slots
 */
export function getWhenFilterSummary(shiftTimeSlots: TimeSlot[], dates: string[]) {
  const datesSummary = getDatesSummary(dates);

  if (shiftTimeSlots.length === 0) {
    return datesSummary ? `${datesSummary}` : "All times";
  }

  return {
    primary: datesSummary,
    secondary: getTimeSlotsSummary(shiftTimeSlots),
  };
}

/**
 * Combines all filter summaries into primary/secondary display text
 * Truncates secondary text when total length exceeds 40 characters
 */
export function getOverallFilterSummary({
  shiftTimeSlots,
  distance,
  dates,
  licenses,
}: {
  shiftTimeSlots: TimeSlot[];
  distance: number;
  dates: string[];
  licenses: string[];
}) {
  const distanceSummary =
    distance === DEFAULT_DISTANCE_IN_MILES_FILTER ? "" : getDistanceFilterSummary(distance);
  const datesSummary = getDatesSummary(dates);
  const licensesSummary = getDetailsFilterSummary(licenses);
  const timeSlotsSummary = getTimeSlotsSummary(shiftTimeSlots);

  const primary = datesSummary === "" ? "Find shifts" : datesSummary;
  const buildSecondary = (parts: string[]) => parts.filter(Boolean).join("・");

  let secondary = buildSecondary([
    distanceSummary,
    licensesSummary === "All" ? "" : licensesSummary,
    timeSlotsSummary,
  ]);

  const totalLength = primary.length + secondary.length;

  if (totalLength >= 40) {
    secondary = buildSecondary([distanceSummary, timeSlotsSummary, `+${licenses.length} more`]);
  }

  return {
    primary,
    secondary,
  };
}
